import { useCallback, useState } from 'react';
import { AgenciesService } from '@/services/requests/housien-services/AgenciesService';
import { convertFileToWebP } from '@/shared/utils/images-manipulation';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import type { PresignedPostResponse } from '@/services/requests/housien-models/PresignedPostResponse';

const MIN_LOGO_BYTES = 1024;
const MAX_LOGO_BYTES = 62_914_560;

export type AgencyLogoStatus = 'idle' | 'converting' | 'uploading' | 'success' | 'error';

export interface UseAgencyLogoUpload {
  upload: (file: File) => Promise<void>;
  remove: () => Promise<void>;
  status: AgencyLogoStatus;
  error: string | null;
}

/**
 * Validates a logo file size against the allowed range.
 *
 * @param bytes - The file size in bytes.
 * @returns A localized error message, or null when the size is valid.
 */
function sizeError(bytes: number): string | null {
  if (bytes < MIN_LOGO_BYTES) return 'A imagem é muito pequena (mínimo 1 KB).';
  if (bytes > MAX_LOGO_BYTES) return 'A imagem excede o limite de 60 MB após a conversão.';
  return null;
}

/**
 * Assembles the multipart form for a presigned S3 POST upload.
 *
 * @param response - The presigned POST response containing url and fields.
 * @param file - The file to upload.
 * @returns The populated FormData ready for the S3 POST.
 */
function buildUploadForm(response: PresignedPostResponse, file: File): FormData {
  const form = new FormData();
  Object.entries(response.fields ?? {})
    .filter(([, value]) => typeof value === 'string')
    .forEach(([key, value]) => form.append(key, value));
  form.append('file', file);
  return form;
}

/**
 * Uploads the logo file to S3 using the presigned POST descriptor.
 *
 * @param response - The presigned POST response with url and fields.
 * @param file - The converted logo file to upload.
 * @throws When the presigned descriptor is incomplete or the upload fails.
 */
async function postLogoToS3(response: PresignedPostResponse, file: File): Promise<void> {
  if (!response.url || !response.fields) throw new Error('Resposta inválida ao gerar o link de upload.');
  const result = await fetch(response.url, { method: 'POST', body: buildUploadForm(response, file) });
  if (!result.ok) throw new Error('Falha ao enviar a logo para o armazenamento.');
}

/**
 * Extracts a human-readable message from an unknown error value.
 *
 * @param err - The caught error, of unknown type.
 * @returns The error message, or a generic fallback message.
 */
function errorMsg(err: unknown): string {
  return err instanceof Error ? err.message : 'Não foi possível enviar a logo.';
}

/**
 * Converts, validates, and uploads a logo file for the given agency.
 *
 * @param agencyId - The identifier of the target agency.
 * @param file - The raw logo file selected by the user.
 * @returns An error message when validation fails, or an empty string on success.
 */
async function doUpload(agencyId: string, file: File): Promise<string> {
  const webp = await convertFileToWebP(file);
  const invalid = sizeError(webp.size);
  if (invalid) return invalid;
  const link = await AgenciesService.generateAgencyLogoUploadUrl(agencyId);
  await postLogoToS3(link, webp);
  return '';
}

/**
 * Manages the agency logo upload/remove lifecycle with status and error state.
 *
 * @param agencyId - The identifier of the agency whose logo is managed.
 * @returns Handlers to upload and remove the logo plus current status and error.
 */
export function useAgencyLogoUpload(agencyId: string): UseAgencyLogoUpload {
  const { updateAgency, refreshFetch } = useAgency((state) => state);
  const [status, setStatus] = useState<AgencyLogoStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(async (file: File) => {
    setError(null);
    setStatus('converting');
    try {
      const invalid = await doUpload(agencyId, file);
      if (invalid) { setStatus('error'); setError(invalid); return; }
      setStatus('uploading');
      const view = await AgenciesService.getAgencyLogoViewUrl(agencyId);
      updateAgency({ logo: view.url });
      refreshFetch();
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(errorMsg(err));
    }
  }, [agencyId, updateAgency, refreshFetch]);

  const remove = useCallback(async () => {
    setError(null);
    try {
      await AgenciesService.deleteAgencyLogo(agencyId);
      updateAgency({ logo: undefined });
      refreshFetch();
      setStatus('idle');
    } catch (err) {
      setStatus('error');
      setError(errorMsg(err));
    }
  }, [agencyId, updateAgency, refreshFetch]);

  return { upload, remove, status, error };
}
